/**
 * @fileoverview A labelled reason text area for the My Page action dialogs.
 * @description The stage, reopen, reassign and request-reassignment dialogs all take a free-text
 * reason of up to 500 characters. This is the one text area they share, with its label, optional
 * hint and validation message.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import Label from '@/shared/components/atoms/Label'

import type { TextareaHTMLAttributes } from 'react'

interface MyPageReasonFieldProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'className' | 'id'
> {
  id: string
  label: string
  error?: string | undefined
  'data-testid'?: string
}

/**
 * @component
 * @param {MyPageReasonFieldProps} props - The field's id, label, error and text-area props.
 * @param {string} props.id - The text area's id (also its label's target).
 * @param {string} props.label - The field's label.
 * @param {string} [props.error] - The validation message to show under it.
 * @param {string} [props['data-testid']] - QA locator; the message gets `<testId>-error`.
 */
function MyPageReasonField({
  id,
  label,
  error,
  'data-testid': testId,
  ...textareaProps
}: MyPageReasonFieldProps) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <textarea
        id={id}
        rows={3}
        aria-invalid={Boolean(error)}
        data-testid={testId}
        className="mt-2 w-full rounded-lg border border-input-border bg-white px-3 py-2 text-[13.5px] text-ink focus:outline-none focus:ring-2 focus:ring-primary/20"
        {...textareaProps}
      />
      {error && (
        <p
          className="mt-1.5 text-[13px] text-red-600"
          data-testid={testId ? `${testId}-error` : undefined}
        >
          {error}
        </p>
      )}
    </div>
  )
}

export default MyPageReasonField
